import { useState } from "react";
import { DoctorDto, doctorKeyId } from "../types/doctor";
import { doctorService } from "../services/doctorService";
import { useApi } from "./useApi";
import { useMutation } from "./useMutation";

export const useDoctors = () => {
  const {
    data: doctors = [],
    loading: isFetching,
    error: getError,
    refetch: fetchDoctors,
    invalidateCache,
  } = useApi<DoctorDto[]>("doctors", (signal) => doctorService.getAll({}, signal));

  // --- ΝΕΟ: Hook για να τραβάμε τα μέλη της ιατρικής ομάδας (από το /api/doctor/members) ---
  const {
    data: medicalTeamMembers = [],
    loading: isFetchingMembers,
    refetch: fetchMedicalTeamMembers,
  } = useApi<DoctorDto[]>("doctors-members", (signal) => 
    // Υποθέτοντας ότι θα προσθέσεις τη μέθοδο getMembers στο doctorService σου:
    doctorService.getMembers ? doctorService.getMembers(signal) : Promise.resolve([])
  );

  const {
    execute: createExecution,
    loading: isCreating,
    error: createError,
  } = useMutation((newDoctor: Record<string, any>) => doctorService.create(newDoctor));

  const {
    execute: updateExecution,
    loading: isUpdating,
    error: updateError,
  } = useMutation(({ id, data }: { id: number | string; data: DoctorDto }) =>
    doctorService.update(id, data)
  );

  const {
    execute: deleteExecution,
    loading: isDeleting,
    error: deleteError,
  } = useMutation(async (ids: number[]) => {
    await Promise.all(ids.map((id) => doctorService.delete(id)));
    return ids;
  });

  const [modalState, setModalState] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    type: "success" | "error";
  }>({
    isOpen: false,
    title: "",
    message: "",
    type: "success",
  });

  const closeModal = () => {
    setModalState((prev) => ({ ...prev, isOpen: false }));
  };

  const createDoctor = async (newDoctor: Record<string, any>): Promise<void> => {
    try {
      await createExecution(newDoctor);
      invalidateCache();
      setModalState({
        isOpen: true,
        title: "Επιτυχία",
        message: "Ο γιατρός αποθηκεύτηκε επιτυχώς!",
        type: "success",
      });
      fetchDoctors(true);
      fetchMedicalTeamMembers(true); // Ανανεώνουμε και τα μέλη αν χρειαστεί
    } catch (err: any) {
      const errorMsg = err.message || "Σφάλμα δημιουργίας";
      setModalState({
        isOpen: true,
        title: "Σφάλμα",
        message: errorMsg,
        type: "error",
      });
    }
  };

  const updateDoctor = async (id: number | string, updatedData: DoctorDto): Promise<void> => {
    try {
      await updateExecution({ id, data: updatedData });
      invalidateCache();
      setModalState({
        isOpen: true,
        title: "Επιτυχία",
        message: "Η ενημέρωση του γιατρού ολοκληρώθηκε επιτυχώς!",
        type: "success",
      });
      fetchDoctors(true);
      fetchMedicalTeamMembers(true);
    } catch (err: any) {
      const errorMsg = err.message || "Σφάλμα ενημέρωσης";
      setModalState({
        isOpen: true,
        title: "Σφάλμα",
        message: errorMsg,
        type: "error",
      });
    }
  };

  const deleteDoctors = async (ids: number[]): Promise<void> => {
    try {
      await deleteExecution(ids);
      invalidateCache();
      setModalState({
        isOpen: true,
        title: "Επιτυχία",
        message: "Η διαγραφή των γιατρών ολοκληρώθηκε επιτυχώς!",
        type: "success",
      });
      fetchDoctors(true);
      fetchMedicalTeamMembers(true);
    } catch (err: any) {
      const errorMsg = err.message || "Σφάλμα κατά τη μαζική διαγραφή";
      setModalState({
        isOpen: true,
        title: "Σφάλμα",
        message: errorMsg,
        type: "error",
      });
    }
  };

  return {
    doctors,
    medicalTeamMembers, // <-- Το επιστρέφουμε για να το χρησιμοποιείς όπου θες
    isLoading: isFetching || isFetchingMembers || isCreating || isUpdating || isDeleting,
    error: getError || createError || updateError || deleteError,
    fetchDoctors: () => fetchDoctors(true),
    fetchMedicalTeamMembers: () => fetchMedicalTeamMembers(true),
    createDoctor,
    updateDoctor,
    deleteDoctors,
    modalState,
    closeModal,
  };
};